/**
 * hmm.js — 4-State Hidden Markov Model for Knowledge State Estimation
 *
 * Implements Viterbi decoding with fixed BKT-derived parameters.
 * See HMM_DOCUMENTATION.md in the project root for full theoretical background,
 * assumptions, and literature references.
 */

// ─────────────────────────────────────────────────────────────────────────────
// State definitions
// ─────────────────────────────────────────────────────────────────────────────

export const STATES = ['Not Learned', 'Learning', 'Mastered', 'Forgetting'];
export const NUM_STATES = 4;

// State indices for readability
const S_NOT_LEARNED  = 0;
const S_LEARNING     = 1;
const S_MASTERED     = 2;
const S_FORGETTING   = 3;

// Observation indices
const O_INCORRECT = 0;
const O_CORRECT   = 1;

// ─────────────────────────────────────────────────────────────────────────────
// Initial state distribution π
// ─────────────────────────────────────────────────────────────────────────────
//
// We assume a new topic is most likely Not Learned (0.70) with some chance the
// student has prior exposure (Learning = 0.20) or was already taught it
// elsewhere (Mastered = 0.05). Forgetting at t=0 is negligible (0.05).
//
// Reference: Corbett & Anderson (1994), "Knowledge Tracing", User Modeling.
//
export const PI = [0.70, 0.20, 0.05, 0.05];

// ─────────────────────────────────────────────────────────────────────────────
// Transition matrix A  (A[from][to])
// ─────────────────────────────────────────────────────────────────────────────
//
// Row sums must equal 1.0.
//
//                     NL     LR     MA     FG
// From Not Learned: [0.85,  0.13,  0.01,  0.01]
//   A student stuck in Not Learned is very likely to stay there (0.85),
//   occasionally transitions into Learning (0.13). Direct jumps to Mastered
//   or Forgetting from NL are rare (0.01 each).
//
// From Learning:    [0.05,  0.55,  0.38,  0.02]
//   Learning is a transient state: probability of staying is 0.55,
//   graduating to Mastered is 0.38 (BKT "learn" rate pL ≈ 0.40 per trial),
//   slipping back to Not Learned is 0.05 (rare regression).
//
// From Mastered:    [0.01,  0.04,  0.85,  0.10]
//   Mastery is persistent (0.85 self-loop) but has a realistic forgetting
//   hazard of 0.10 that moves the student to Forgetting — consistent with
//   Ebbinghaus decay and spaced-repetition literature.
//
// From Forgetting:  [0.05,  0.35,  0.20,  0.40]
//   Forgetting state is semi-stable (0.40 self-loop). A reminder can quickly
//   restore partial knowledge (Learning 0.35) or full mastery (0.20).
//   Pure decay back to Not Learned is low (0.05).
//
// References:
//   - Corbett & Anderson (1994) — two-state BKT provides pL, pF base rates
//   - Khajah et al. (2016) — extensions with forgetting state
//   - Pavlik & Anderson (2005) — ACT-R decay model informs forgetting hazard
//
export const A = [
  [0.85, 0.13, 0.01, 0.01],  // Not Learned →
  [0.05, 0.55, 0.38, 0.02],  // Learning    →
  [0.01, 0.04, 0.85, 0.10],  // Mastered    →
  [0.05, 0.35, 0.20, 0.40],  // Forgetting  →
];

// ─────────────────────────────────────────────────────────────────────────────
// Emission matrix B  (B[state][observation])
// ─────────────────────────────────────────────────────────────────────────────
//
// B[state][O_INCORRECT]  B[state][O_CORRECT]
//
// Not Learned:  [0.85, 0.15]
//   A student who has not learned the topic answers correctly only 15% of the
//   time (guessing). BKT typical guess rate g ≈ 0.10–0.25; we use 0.15.
//
// Learning:     [0.45, 0.55]
//   A student actively learning answers correctly slightly more than not,
//   reflecting partial knowledge acquisition.
//
// Mastered:     [0.10, 0.90]
//   High accuracy for mastered topics; 0.10 slip probability consistent with
//   BKT typical slip rate s ≈ 0.05–0.15.
//
// Forgetting:   [0.55, 0.45]
//   A forgetting student answers slightly worse than chance, reflecting lost
//   but partially recoverable knowledge.
//
// References:
//   - Corbett & Anderson (1994): g (guess) ≈ 0.15–0.25, s (slip) ≈ 0.10
//   - Baker et al. (2008) — slip/guess rates analysis in ASSISTments
//
export const B = [
  [0.85, 0.15],  // Not Learned
  [0.45, 0.55],  // Learning
  [0.10, 0.90],  // Mastered
  [0.55, 0.45],  // Forgetting
];

// ─────────────────────────────────────────────────────────────────────────────
// State metadata for display
// ─────────────────────────────────────────────────────────────────────────────

export const STATE_META = [
  { label: 'Not Learned',  emoji: '❌', color: '#ef4444', bg: 'rgba(239,68,68,0.15)'   },
  { label: 'Learning',     emoji: '📈', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)'  },
  { label: 'Mastered',     emoji: '✅', color: '#10b981', bg: 'rgba(16,185,129,0.15)'  },
  { label: 'Forgetting',   emoji: '🌀', color: '#8b5cf6', bg: 'rgba(139,92,246,0.15)'  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Viterbi algorithm
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Run the Viterbi algorithm on a binary observation sequence.
 *
 * @param {number[]} observations  Array of 0 (incorrect) or 1 (correct)
 * @returns {{ stateIndex: number, stateName: string, confidence: number, path: number[] } | null}
 *   Returns null if observations array is empty.
 *
 * Algorithm:
 *   1. Initialise: δ[s] = π[s] · B[s][o₀]
 *   2. Recurse:    δ_t[s] = max_j(δ_{t-1}[j] · A[j][s]) · B[s][oₜ]
 *   3. Terminate:  best final state = argmax δ_T
 *   4. Backtrack:  reconstruct optimal state path via ψ pointers
 *
 * Computation is performed in log-space to avoid floating-point underflow
 * on long sequences.
 */
export function viterbi(observations) {
  const T = observations.length;
  if (T === 0) return null;

  const N = NUM_STATES;

  // δ[t][s] = log probability of most likely path ending in state s at time t
  const delta = Array.from({ length: T }, () => new Array(N).fill(-Infinity));
  // ψ[t][s] = previous state that led to maximum δ[t][s]
  const psi   = Array.from({ length: T }, () => new Array(N).fill(0));

  // ── Initialisation ──────────────────────────────────────────────────────────
  const o0 = observations[0]; // 0 or 1
  for (let s = 0; s < N; s++) {
    delta[0][s] = Math.log(PI[s]) + Math.log(B[s][o0] + 1e-300);
    psi[0][s]   = 0;
  }

  // ── Recursion ────────────────────────────────────────────────────────────────
  for (let t = 1; t < T; t++) {
    const ot = observations[t];
    for (let s = 0; s < N; s++) {
      let maxVal  = -Infinity;
      let maxPrev = 0;
      for (let j = 0; j < N; j++) {
        const val = delta[t - 1][j] + Math.log(A[j][s] + 1e-300);
        if (val > maxVal) {
          maxVal  = val;
          maxPrev = j;
        }
      }
      delta[t][s] = maxVal + Math.log(B[s][ot] + 1e-300);
      psi[t][s]   = maxPrev;
    }
  }

  // ── Termination ──────────────────────────────────────────────────────────────
  let bestState     = 0;
  let bestLogProb   = -Infinity;
  for (let s = 0; s < N; s++) {
    if (delta[T - 1][s] > bestLogProb) {
      bestLogProb = delta[T - 1][s];
      bestState   = s;
    }
  }

  // ── Backtracking ─────────────────────────────────────────────────────────────
  const path = new Array(T);
  path[T - 1] = bestState;
  for (let t = T - 2; t >= 0; t--) {
    path[t] = psi[t + 1][path[t + 1]];
  }

  // ── Confidence: normalised probability of best state at final step ───────────
  // Convert log-probabilities back to probabilities and softmax-normalise
  const logProbs = delta[T - 1];
  const maxLog   = Math.max(...logProbs);
  const expProbs = logProbs.map(lp => Math.exp(lp - maxLog)); // shift for stability
  const sumExp   = expProbs.reduce((a, b) => a + b, 0);
  const confidence = expProbs[bestState] / sumExp; // in [0, 1]

  return {
    stateIndex: bestState,
    stateName:  STATES[bestState],
    confidence,
    path,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalise a subject string to lowercase-trimmed for case-insensitive grouping.
 */
function normaliseKey(subject) {
  return (subject || 'unknown').trim().toLowerCase();
}

/**
 * Normalise a single record from either source into a unified shape:
 *   { correct: 0|1, recorded_at: string, _source: 'formal'|'informal' }
 *
 * student_mastery_history  →  correct field is a number (0 or 1)
 * question_answered        →  correct field is the string "correct" or "wrong"
 */
function normaliseRecord(record, source) {
  let isCorrect;
  if (source === 'formal') {
    isCorrect = Number(record.correct) > 0;
  } else {
    // question_answered uses string values
    isCorrect = record.correct === 'correct' || record.correct === true;
  }
  return {
    correct:      isCorrect ? O_CORRECT : O_INCORRECT,
    recorded_at:  record.recorded_at || '',
    _source:      source,
  };
}

/**
 * Downsample an array keeping every Nth element (deterministic, index-based).
 * Used to apply 0.5× weight to informal records: keep every other one.
 */
function downsample(arr, keepEvery = 2) {
  return arr.filter((_, i) => i % keepEvery === 0);
}

/**
 * Merge formal (mastery_history) and informal (question_answered) records for
 * a single topic, sorted by recorded_at, with informal records downsampled to
 * 50% to reflect their lower reliability.
 *
 * @param {object[]} formalRecords    From student_mastery_history
 * @param {object[]} informalRecords  From question_answered
 * @returns {number[]}  Binary observation sequence (0=incorrect, 1=correct)
 */
export function mergeAndBuildObservations(formalRecords, informalRecords) {
  const formal   = formalRecords.map(r => normaliseRecord(r, 'formal'));
  const informal = downsample(
    informalRecords.map(r => normaliseRecord(r, 'informal'))
  );

  const merged = [...formal, ...informal]
    .sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at));

  return merged.map(r => r.correct);
}

/**
 * Legacy helper — formal records only, sorted by time.
 * Kept for backwards compatibility.
 *
 * @param {Array<{ correct: number, wrong: number, recorded_at: string }>} records
 * @returns {number[]}
 */
export function recordsToObservations(records) {
  return records
    .slice()
    .sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at))
    .map(r => (Number(r.correct) > 0 ? O_CORRECT : O_INCORRECT));
}

/**
 * Group masteryHistory (and optionally questionAnsweredHistory) by subject and
 * run Viterbi on each topic.
 *
 * Informal (question_answered) records are merged at 0.5× weight by keeping
 * every other record after sorting by time. This reflects the lower reliability
 * of chat-based Q&A vs. formal quiz answers.
 *
 * Subject matching uses case-insensitive keys so minor normalisation drift
 * between the two tables is absorbed automatically.
 *
 * @param {Array<{ subject, correct, wrong, recorded_at }>} masteryHistory
 *   Formal quiz records from student_mastery_history.
 * @param {number} [minObservations=5]
 *   Topics with fewer total observations (after merge) are marked insufficient.
 * @param {Array<{ subject, correct, recorded_at }>} [questionAnsweredHistory=[]]
 *   Informal chat Q&A records from question_answered. Optional.
 * @returns {Array<{ subject, result, observationCount, formalCount, informalCount, insufficient }>}
 */
export function runHMMForAllTopics(masteryHistory, minObservations = 5, questionAnsweredHistory = []) {
  const hasFormal   = masteryHistory && masteryHistory.length > 0;
  const hasInformal = questionAnsweredHistory && questionAnsweredHistory.length > 0;

  if (!hasFormal && !hasInformal) return [];

  // ── Group formal records by normalised subject key ──────────────────────────
  const formalByKey    = {};  // key → { canonicalSubject, records[] }
  if (hasFormal) {
    for (const entry of masteryHistory) {
      const key = normaliseKey(entry.subject);
      if (!formalByKey[key]) formalByKey[key] = { canonicalSubject: entry.subject, records: [] };
      formalByKey[key].records.push(entry);
    }
  }

  // ── Group informal records by normalised subject key ────────────────────────
  const informalByKey = {};
  if (hasInformal) {
    for (const entry of questionAnsweredHistory) {
      const key = normaliseKey(entry.subject);
      if (!informalByKey[key]) informalByKey[key] = [];
      informalByKey[key].push(entry);
    }
  }

  // ── Union of all subject keys ────────────────────────────────────────────────
  const allKeys = [...new Set([
    ...Object.keys(formalByKey),
    ...Object.keys(informalByKey),
  ])];

  return allKeys.map(key => {
    const formalEntry    = formalByKey[key];
    const formalRecords  = formalEntry   ? formalEntry.records : [];
    const informalRecords = informalByKey[key] || [];

    // Use the canonical subject name from formal records if available,
    // otherwise fall back to the first informal record's subject.
    const subject = formalEntry
      ? formalEntry.canonicalSubject
      : (informalRecords[0]?.subject || key);

    const observations   = mergeAndBuildObservations(formalRecords, informalRecords);
    const formalCount    = formalRecords.length;
    // informalCount after 50% downsampling
    const informalCount  = downsample(informalRecords).length;
    const totalCount     = observations.length;

    if (totalCount < minObservations) {
      return {
        subject,
        result:         null,
        observationCount: totalCount,
        formalCount,
        informalCount,
        insufficient:   true,
      };
    }

    const result = viterbi(observations);
    return {
      subject,
      result,
      observationCount: totalCount,
      formalCount,
      informalCount,
      insufficient: false,
    };
  }).sort((a, b) => {
    if (a.insufficient && !b.insufficient) return 1;
    if (!a.insufficient && b.insufficient) return -1;
    if (a.result && b.result) return a.result.stateIndex - b.result.stateIndex;
    return 0;
  });
}
